import { useState } from 'react';
import { Button } from './primitives';
import { useActiveTeam } from '../lib/queries/useActiveTeam';
import { useTeamPlayers } from '../lib/queries/useTeamPlayers';
import { sharePlayerNote } from '../lib/playerFeedback';
import './playerFeedback.css';

interface Props {
  sessionId: string;
  coachId: string | null;
}

/**
 * The consent gate for coach → player feedback: the coach's session review
 * notes stay private; ONLY what they explicitly share here reaches a player.
 * One share = one opt-in action. (Persists to session_player_notes, 00089.)
 */
export function SessionPlayerNoteShare({ sessionId, coachId }: Props) {
  const { team } = useActiveTeam();
  const { rows: players } = useTeamPlayers(team?.id, false, { limit: 40 });
  const [playerId, setPlayerId] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  if (players.length === 0) return null; // nothing to share to yet

  const onShare = async () => {
    if (!team || !coachId || !playerId || !note.trim()) { setMsg('Pick a player and write a note.'); return; }
    setBusy(true);
    setMsg(null);
    const res = await sharePlayerNote({ sessionId, teamId: team.id, playerId, coachId, note });
    setBusy(false);
    if (!res.ok) { setMsg(res.error ?? 'Could not share.'); return; }
    const name = players.find((p) => p.id === playerId)?.name ?? 'the player';
    setMsg(`Shared with ${name}.`);
    setNote('');
    setPlayerId('');
  };

  return (
    <div className="pf-share">
      <span className="pf-share__label">Share a note with a player</span>
      <p className="pf-share__hint">
        Optional — your reflections above stay private. Only what you share here reaches that player.
      </p>
      <div className="pf-share__row">
        <select className="pf-share__select" value={playerId} onChange={(e) => setPlayerId(e.target.value)} aria-label="Player">
          <option value="">Select player…</option>
          {players.map((p) => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </select>
        <textarea
          className="pf-share__note"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={2}
          placeholder="e.g. Loved your work rate pressing from the front today — keep it up."
        />
        <Button variant="primary" size="workbench" onClick={() => void onShare()} disabled={busy || !playerId || !note.trim()}>
          {busy ? 'Sharing…' : 'Share'}
        </Button>
      </div>
      {msg && <span className="pf-share__msg">{msg}</span>}
    </div>
  );
}
